import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock } from "lucide-react";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import PageHero from "@/components/shared/PageHero";
import CtaSection from "@/components/shared/CtaSection";
import { Stagger, StaggerItem } from "@/components/motion/Reveal";
import { POSTS, BLOG_CATEGORIES } from "@/data/blog";

export default function BlogHub() {
  const [cat, setCat] = useState("All");
  const filtered = cat === "All" ? POSTS : POSTS.filter((p) => p.category === cat);

  return (
    <>
      <Seo
        title="GEO & AI Search Resource Center"
        description="Insights, guides and comparisons on Generative Engine Optimization, AI search, ChatGPT, Gemini, Claude, Perplexity, semantic SEO and structured data from CompuBrain AI."
        path="/blog"
        schemas={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }])]}
      />
      <PageHero
        eyebrow="Resource Center"
        titleLines={["Insights for the", "AI search era."]}
        sub="Guides, comparisons and strategy on Generative Engine Optimization and AI-powered search — written by practitioners."
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Blog" }]}
      />
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <div className="flex flex-wrap gap-2 mb-12">
          {["All", ...BLOG_CATEGORIES].map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`blog-filter-${c}`}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${cat === c ? "bg-[#111111] text-white" : "border border-gray-200 text-gray-600 hover:border-[#111111]"}`}
            >
              {c}
            </button>
          ))}
        </div>
        <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((p) => (
            <StaggerItem key={p.slug}>
              <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group flex flex-col h-full rounded-2xl border border-gray-200 overflow-hidden hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(0,0,0,0.06)] transition-all duration-500">
                <div className="aspect-[16/9] bg-[#111111] relative overflow-hidden">
                  <div className="absolute inset-0 bg-grid-dark opacity-50" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="font-display text-2xl font-black text-white/90 px-6 text-center tracking-tight">{p.category}</span>
                  </div>
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="font-bold uppercase tracking-[0.15em] text-[#0B63F6]">{p.category}</span>
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {p.readingTime} min</span>
                  </div>
                  <h2 className="font-display text-xl font-bold tracking-tight mt-4 leading-snug">{p.title}</h2>
                  <p className="text-[15px] text-gray-600 mt-3 leading-relaxed flex-1">{p.excerpt}</p>
                  <span className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-[#0B63F6]">Read article <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
                </div>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </section>
      <CtaSection heading="Turn insight into AI visibility" sub="Reading about GEO is a start. Let's implement it for your brand." />
    </>
  );
}
